import { createReferenceResolutionContextV3, type CanvasDocumentV3, type ReferenceBindingDiagnostic } from "@seedance/contracts";
import {
  finalizeCompiledSeedanceRequest, validateCompiledAsset, validateSettings, validateSeedanceRequestContent,
  type CompileSeedanceFailure, type CompileSeedanceRequestInput, type CompileSeedanceResult
} from "./seedance-compiler.js";
import type { ReferenceMapItem } from "./reference-map.js";

/** A check is not a submission snapshot and deliberately has no request or fingerprint. */
export type CheckSeedanceResult = CompileSeedanceFailure | {
  ok: true;
  finalPrompt: string;
  references: readonly ReferenceMapItem[];
};

type CompilerOptions = Omit<CompileSeedanceRequestInput, "document">;
type ValidatedInput = CompileSeedanceFailure | {
  ok: true;
  target: Extract<CanvasDocumentV3["nodes"][number], { kind: "seedance" }>;
  references: ReferenceMapItem[];
  finalPrompt: string;
};

function bindingFailure(diagnostics: ReferenceBindingDiagnostic[]): CompileSeedanceFailure {
  return {
    ok: false, code: "INVALID_REFERENCE_BINDING",
    message: "引用素材缺失或绑定无效，请重新选择素材。",
    nodeIds: [...new Set(diagnostics.map((item) => item.targetNodeId))],
    edgeIds: [...new Set(diagnostics.flatMap((item) => item.edgeIds ?? []))],
    request: undefined
  };
}

/** Pure, provider-independent V3 compilation. Identity resolution is contracts-owned;
 * authorization and model limits remain mandatory server-side checks. */
export function createSeedanceRequestCompilerV3(input: CanvasDocumentV3) {
  const document = structuredClone(input);
  const context = createReferenceResolutionContextV3(document);
  let targets: Map<string, CanvasDocumentV3["nodes"][number]> | undefined;
  const validateInput = ({ seedanceNodeId, ownerId, capabilities, assets }: CompilerOptions): ValidatedInput => {
    const resolved = context.resolve(seedanceNodeId);
    if (!resolved.ready) return bindingFailure(resolved.diagnostics);
    targets ??= new Map(document.nodes.map(node => [node.id, node]));
    const target = targets.get(seedanceNodeId);
    // The resolver requires a Seedance target before it can be ready.
    if (target?.kind !== "seedance") return bindingFailure(resolved.diagnostics);
    const settingsFailure = validateSettings(target.settings, capabilities);
    if (settingsFailure) return { ...settingsFailure, nodeIds: [target.id] };
    for (const reference of resolved.references) {
      const assetFailure = validateCompiledAsset(reference, target.id, ownerId, assets, capabilities);
      if (assetFailure) return assetFailure;
    }
    return { ok: true, target, references: resolved.references, finalPrompt: resolved.finalPrompt };
  };
  const check = (options: CompilerOptions): CheckSeedanceResult => {
    const value = validateInput(options);
    if (!value.ok) return value;
    return validateSeedanceRequestContent({ ...value, capabilities: options.capabilities })
      ?? { ok: true, finalPrompt: value.finalPrompt, references: value.references };
  };
  const compile = (options: CompilerOptions): CompileSeedanceResult => {
    const value = validateInput(options);
    if (!value.ok) return value;
    return finalizeCompiledSeedanceRequest({ ...value, capabilities: options.capabilities, compilerVersion: "2026-09-07-v3" });
  };
  return Object.freeze({ resolve: context.resolve, check, compile });
}

export function compileSeedanceRequestV3({ document, ...options }:
  Omit<CompileSeedanceRequestInput, "document"> & { document: CanvasDocumentV3 }
): CompileSeedanceResult {
  return createSeedanceRequestCompilerV3(document).compile(options);
}
